import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styles from './chat-agent-overlay.scss';
import './chat-agent-global.css';

import ChatAgentService from '../../services/chatAgentService';

import {
  toggleChatAgent,
  sendMessage,
  createConversation,
  loadUserConversations,
  switchConversation,
  deleteConversation,
  updateConversationTitle,
  setActiveConversation,
  setLastEmptyConversation,
  fetchChatFilters,
  updateMessageFeedback,
  regenerateMessage,
  submitCategorySelection,
} from "../../containers/chat-agent/actions";

// Import extracted components
import { TemplateBuilder, TemplateGrid, getTemplateQuestions, getTemplatesForScreen } from './chat-templates';
import ChatInput from './chat-input';
import ConversationsSidebar from './components/ConversationsSidebar';
import ChatHeader from './components/ChatHeader';
import MessagesArea from './components/MessagesArea';

// Import custom hooks
import { useAutoScroll } from './hooks/useAutoScroll';
import { useConversationPersistence } from './hooks/useConversationPersistence';
import { useTemplateHandling } from './hooks/useTemplateHandling';
import { useConversationManagement } from './hooks/useConversationManagement';

// Import utilities
import { clearChatFromHash } from './utils/conversationUtils';
import { exportConversationWithFeedback } from './utils/pdfExportV2';

function ChatAgentOverlay(props) {
  const {
    // Core state from chatAgent
    isOpen,
    messages,
    isLoading,
    error,
    currentScreen,
    // Conversation state
    activeConversationId,
    conversations,
    conversationsList,
    isLoadingConversations,
    isCreatingConversation,
    isDeletingConversation,
    // Pipeline state
    pipelineActive,
    pipelineComplete,
    pipelineError,
    stages,
    currentStage,
    agentMessages,
    thinkingStreams,
    // Actions
    toggleChatAgent,
    sendMessage,
    createConversation,
    loadUserConversations,
    switchConversation,
    deleteConversation,
    updateConversationTitle,
    setActiveConversation,
    updateMessageFeedback,
    regenerateMessage,
    submitCategorySelection,
  } = props;

  // Local state
  const [showTemplates, setShowTemplates] = useState(false);
  const [showConversationsSidebar, setShowConversationsSidebar] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [themeMode, setThemeMode] = useState(() => {
    // Load saved preference or default to 'system'
    return localStorage.getItem('chatAgentTheme') || 'system';
  });
  const [messageQueue, setMessageQueue] = useState([]);

  // Detect system theme preference
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Compute effective light mode based on theme setting
  const isLightMode = themeMode === 'light' || (themeMode === 'system' && !systemPrefersDark);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPrefersDark(e.matches);
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } 
    // Older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, []);

  // Refs
  const overlayRef = useRef(null);
  const chatInputRef = useRef(null);
  const isProcessingRef = useRef(false);
  
  // Service instance
  const chatAgentService = new ChatAgentService();

  // Custom hooks for business logic
  const messagesEndRef = useAutoScroll(messages, isLoading);
  
  useConversationPersistence(activeConversationId, messages);
  
  // Auto-send queued messages when processing completes
  useEffect(() => {
    const wasProcessing = isProcessingRef.current;
    const isCurrentlyProcessing = isLoading || pipelineActive;
    
    isProcessingRef.current = isCurrentlyProcessing;
    
    // If we were processing and now we're not, send the next queued message
    if (wasProcessing && !isCurrentlyProcessing && messageQueue.length > 0) {
      const [nextMessage, ...remainingQueue] = messageQueue;
      setMessageQueue(remainingQueue);
      
      // Send the next message
      console.log('📤 Sending queued message:', nextMessage);
      sendMessage(nextMessage);
    }
  }, [isLoading, pipelineActive, messageQueue, sendMessage]);
  
  const {
    selectedTemplate,
    inputValue,
    setInputValue,
    handleTemplateClick,
    handleTemplateSubmit,
    handleTemplateCancel
  } = useTemplateHandling({
    activeConversationId,
    messages,
    createConversation,
    switchConversation,
    loadUserConversations,
    chatInputRef,
    setShowTemplates,
    handleSendMessage: (msg) => {
      if (msg.trim()) {
        const isCurrentlyProcessing = isLoading || pipelineActive;
        
        if (isCurrentlyProcessing) {
          // Queue the message
          console.log('⏳ Queuing message (template):', msg);
          setMessageQueue(prev => [...prev, msg]);
        } else {
          // Send immediately
          sendMessage(msg);
        }
        
        setShowTemplates(false);
        setInputValue('');
      }
    }
  });
  
  const {
    handleConversationSelect,
    handleNewConversation,
    handleDeleteConversation
  } = useConversationManagement({
    isOpen,
    activeConversationId,
    conversationsList,
    createConversation,
    switchConversation,
    deleteConversation,
    loadUserConversations,
    conversations,
    chatInputRef
  });

  // Event handlers
  const handleSendMessage = (message) => {
    if (message.trim()) {
      const isCurrentlyProcessing = isLoading || pipelineActive;
      
      if (isCurrentlyProcessing) {
        // Queue the message
        console.log('⏳ Queuing message:', message);
        setMessageQueue(prev => [...prev, message]);
      } else {
        // Send immediately
        sendMessage(message);
      }
      
      setShowTemplates(false);
      setInputValue('');
    }
  };

  const handleClose = () => {
    clearChatFromHash();
    try {
      toggleChatAgent();
    } catch (err) {
      console.error("toggleChatAgent failed:", err);
    }
  };

  const handleToggleTemplates = () => {
    setShowTemplates(!showTemplates);
  };

  const handleToggleConversationsSidebar = () => {
    setShowConversationsSidebar(!showConversationsSidebar);
    if (!showConversationsSidebar) {
      loadUserConversations();
    }
  };
  
  const handleUpdateTitle = async (conversationId, newTitle) => {
    try {
      await updateConversationTitle(conversationId, newTitle);
    } catch (err) {
      console.error('Failed to update title:', err);
    }
  };

  const handleExportToPDF = async (conversationId) => {
    await exportConversationWithFeedback(
      conversationId,
      conversations,
      {
        onStart: () => setIsExporting(true),
        onSuccess: () => {
          setIsExporting(false);
        },
        onError: (err) => {
          setIsExporting(false);
          alert(`Failed to export PDF: ${err.message}`);
        }
      }
    );
  };

  const handleToggleTheme = (mode) => {
    // Accept a specific mode or cycle through modes if no mode provided
    const nextMode = mode || (themeMode === 'dark' ? 'system' : (themeMode === 'system' ? 'light' : 'dark'));
    setThemeMode(nextMode);
    localStorage.setItem('chatAgentTheme', nextMode);
  };

  const handleCategorySelection = (selectedValues, selectionMessage) => {
    submitCategorySelection(selectedValues, selectionMessage);
  };

  const handleFeedback = async (messageId, feedbackType, feedbackText) => {
    try {
      console.log('Submitting feedback:', { messageId, feedbackType, feedbackText, activeConversationId });
      
      if (!activeConversationId) {
        console.error('No active conversation ID available for feedback');
        throw new Error('No active conversation');
      }
      
      await chatAgentService.submitMessageFeedback(messageId, feedbackType, feedbackText, activeConversationId);
      // Update local state to show feedback was submitted
      updateMessageFeedback(messageId, feedbackType, feedbackText);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error;
    }
  };

  const handleRegenerate = (messageId) => {
    // Dispatch the regenerate action - this will handle everything:
    // 1. Remove the old message
    // 2. Reset progress
    // 3. Call API
    // 4. Show new message
    regenerateMessage(messageId);
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Get current messages (from active conversation)
  const currentMessages = messages || [];
  const templateQuestions = getTemplatesForScreen(currentScreen);

  // Template builder view
  if (selectedTemplate) {
    return (
      <div className={`${styles.overlay} ${isLightMode ? styles.lightMode : ''}`}>
        <div className={`${styles.chatWindow} ${isLightMode ? styles.chatWindowLight : ''}`} ref={overlayRef}>
          <ChatHeader
            showConversationsSidebar={false}
            onToggleSidebar={handleToggleConversationsSidebar}
            onToggleTemplates={handleTemplateCancel}
            onClose={handleClose}
            isLightMode={isLightMode}
            onToggleTheme={handleToggleTheme}
            isTemplateView={true}
          />
          <div className={`${styles.templateBuilderContainer} ${isLightMode ? styles.lightMode : ''}`}>
            <TemplateBuilder
              template={selectedTemplate}
              onSubmit={handleTemplateSubmit}
              onCancel={handleTemplateCancel}
              isLightMode={isLightMode}
            />
          </div>
        </div>
      </div>
    );
  }

  // Templates list view
  if (showTemplates) {
    return (
      <div className={`${styles.overlay} ${isLightMode ? styles.lightMode : ''}`}>
        <div className={`${styles.chatWindow} ${isLightMode ? styles.chatWindowLight : ''}`} ref={overlayRef}>
          <ChatHeader
            showConversationsSidebar={false}
            onToggleSidebar={handleToggleConversationsSidebar}
            onToggleTemplates={handleToggleTemplates}
            onClose={handleClose}
            isLightMode={isLightMode}
            onToggleTheme={handleToggleTheme}
            isTemplateView={true}
          />

          <TemplateGrid 
            templates={templateQuestions}
            onTemplateClick={handleTemplateClick}
            currentScreen={currentScreen}
            isLightMode={isLightMode}
          />
        </div>
      </div>
    );
  }

  // Main chat view
  return (
    <div className={`${styles.overlay} ${isLightMode ? styles.lightMode : ''}`}>
      <div className={`${styles.chatWindow} ${showConversationsSidebar ? styles.withSidebar : ''} ${isLightMode ? styles.chatWindowLight : ''}`} ref={overlayRef}>
        {/* Sidebar for conversations */}
        {showConversationsSidebar && (
          <ConversationsSidebar
            conversations={conversationsList}
            activeConversationId={activeConversationId}
            onSelectConversation={handleConversationSelect}
            onCreateNew={handleNewConversation}
            onUpdateTitle={handleUpdateTitle}
            onDeleteConversation={handleDeleteConversation}
            onExportToPDF={handleExportToPDF}
            isLoading={isLoading}
            isLoadingConversations={isLoadingConversations}
            isCreatingConversation={isCreatingConversation}
            isExporting={isExporting}
            isLightMode={isLightMode}
          />
        )}
        
        {/* Main chat area */}
        <div className={`${styles.chatMain} ${isLightMode ? styles.chatMainLight : ''}`}>
          <ChatHeader
            showConversationsSidebar={showConversationsSidebar}
            onToggleSidebar={handleToggleConversationsSidebar}
            onToggleTemplates={handleToggleTemplates}
            onClose={handleClose}
            isLightMode={isLightMode}
            themeMode={themeMode}
            onToggleTheme={handleToggleTheme}
          />

          <MessagesArea
            messages={currentMessages}
            messagesEndRef={messagesEndRef}
            isLoading={isLoading || pipelineActive}
            error={error}
            pipelineActive={pipelineActive}
            pipelineComplete={pipelineComplete}
            pipelineError={pipelineError}
            stages={stages}
            currentStage={currentStage}
            agentMessages={agentMessages}
            thinkingStreams={thinkingStreams}
            isCreatingConversation={isCreatingConversation}
            isDeletingConversation={isDeletingConversation}
            onTemplateClick={handleTemplateClick}
            onToggleTemplates={handleToggleTemplates}
            isLightMode={isLightMode}
            currentScreen={currentScreen}
            onFeedback={handleFeedback}
            onRegenerate={handleRegenerate}
            onCategorySelection={handleCategorySelection}
          />

          <div className={`${styles.inputContainer} ${isLightMode ? styles.inputContainerLight : ''}`}>
            {/* Queue indicator */}
            {messageQueue.length > 0 && (
              <div className={`${styles.queueIndicator} ${isLightMode ? styles.queueIndicatorLight : ''}`}>
                <span className={styles.queueIcon}>⏳</span>
                <span className={styles.queueText}>
                  {messageQueue.length} message{messageQueue.length > 1 ? 's' : ''} queued
                </span>
              </div>
            )}
            
            <ChatInput
              ref={chatInputRef}
              onSendMessage={handleSendMessage}
              disabled={false}
              value={inputValue}
              onChange={setInputValue}
              isLightMode={isLightMode}
              placeholder={
                (isLoading || pipelineActive) 
                  ? "Message will be sent after current response..." 
                  : "Ask me anything about your brand performance..."
              }
            />
          </div>
        </div>
      </div>
      
      {/* PDF Export Loading Overlay */}
      {isExporting && (
        <div className={styles.exportOverlay}>
          <div className={styles.exportModal}>
            <div className={styles.exportSpinner}>
              <div className={styles.spinnerRing}></div>
              <div className={styles.spinnerRing}></div>
              <div className={styles.spinnerRing}></div>
            </div>
            <h3>Generating PDF</h3>
            <p>Please wait while we prepare your document...</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Simplified mapStateToProps - reads ONLY from chatAgent state
const mapStateToProps = (state) => {
  // Extract pipeline from active conversation
  const activeConvId = state.chatAgent?.activeConversationId;
  const activeConv = state.chatAgent?.conversations?.[activeConvId];
  
  // Pipeline state from ACTIVE conversation (falls back to global for backward compatibility)
  const activePipeline = activeConv?.pipeline || {
    pipelineActive: state.chatAgent?.pipelineActive || false,
    pipelineComplete: state.chatAgent?.pipelineComplete || false,
    pipelineError: state.chatAgent?.pipelineError || null,
    stages: state.chatAgent?.stages || [],
    currentStage: state.chatAgent?.currentStage || null,
    agentMessages: state.chatAgent?.agentMessages || [],
    thinkingStreams: state.chatAgent?.thinkingStreams || {},
  };
  
  return {
    // Core chat state
    isOpen: state.chatAgent?.isOpen || false,
    messages: state.chatAgent?.messages || [],
    isLoading: activePipeline.pipelineActive ? false : (state.chatAgent?.isLoading || false),
    error: state.chatAgent?.error || null,
    currentScreen: state.chatAgent?.currentScreen || null,
    
    // Filter state (for templates - decoupled)
    filterDefinitions: state.chatAgent?.filterDefinitions || [],
    isLoadingFilters: state.chatAgent?.isLoadingFilters || false,
    
    // Conversation system state
    activeConversationId: activeConvId,
    lastEmptyConversationId: state.chatAgent?.lastEmptyConversationId,
    conversations: state.chatAgent?.conversations || {},
    conversationsList: state.chatAgent?.conversationsList || [],
    isLoadingConversations: state.chatAgent?.isLoadingConversations || false,
    conversationsError: state.chatAgent?.conversationsError,
    isCreatingConversation: state.chatAgent?.isCreatingConversation || false,
    isDeletingConversation: state.chatAgent?.isDeletingConversation || false,
    
    // Pipeline state from ACTIVE conversation
    pipelineActive: activePipeline.pipelineActive,
    pipelineComplete: activePipeline.pipelineComplete,
    pipelineError: activePipeline.pipelineError,
    stages: activePipeline.stages,
    currentStage: activePipeline.currentStage,
    agentMessages: activePipeline.agentMessages,
    thinkingStreams: activePipeline.thinkingStreams || {},
  };
};

// Simplified mapDispatchToProps
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      toggleChatAgent,
      sendMessage,
      createConversation,
      loadUserConversations,
      switchConversation,
      deleteConversation,
      updateConversationTitle,
      setActiveConversation,
      setLastEmptyConversation,
      fetchChatFilters,
      updateMessageFeedback,
      regenerateMessage,
      submitCategorySelection,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ChatAgentOverlay);
